import { useState, useCallback } from 'react';
import { getModuleAnswers } from '@/lib/simulationResultsApi';
import { mapApiModuleAnswerToDomain } from '@/lib/mappers';
import type { ModuleAnswer } from '@/types/simulation-results';

interface UseModuleExportReturn {
  readonly isExporting: boolean;
  readonly answersMap: Record<string, ModuleAnswer[]> | null;
  readonly error: string | null;
  readonly fetchModulesForExport: (
    sessionId: string,
    modules: string[],
  ) => Promise<Record<string, ModuleAnswer[]> | null>;
  readonly clearExport: () => void;
}

export function useModuleExport(): UseModuleExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [answersMap, setAnswersMap] = useState<Record<string, ModuleAnswer[]> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchModulesForExport = useCallback(
    async (
      sessionId: string,
      modules: string[],
    ): Promise<Record<string, ModuleAnswer[]> | null> => {
      setIsExporting(true);
      setError(null);
      setAnswersMap(null);
      try {
        const entries = await Promise.all(
          modules.map(async (mod) => {
            const raw = await getModuleAnswers(sessionId, mod);
            return [mod, raw.map(mapApiModuleAnswerToDomain)] as const;
          }),
        );
        const map = Object.fromEntries(entries);
        setAnswersMap(map);
        return map;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Export failed');
        return null;
      } finally {
        setIsExporting(false);
      }
    },
    [],
  );

  const clearExport = useCallback(() => {
    setAnswersMap(null);
    setError(null);
  }, []);

  return { isExporting, answersMap, error, fetchModulesForExport, clearExport };
}
